import { writable, derived } from 'svelte/store';
import type { Writable, Readable } from 'svelte/store';
import { EmployerService, type Employer } from '../lib/api/employer.service';
import type { DonationStatus } from '../lib/api/admin/donation.service';

// Interface for store state
interface AppDataState {
  employers: Employer[];
  donationStatuses: DonationStatus[];
  loading: {
    employers: boolean;
    donationStatuses: boolean;
  };
  errors: {
    employers: string | null;
    donationStatuses: string | null;
  };
  lastFetched: {
    employers: number | null;
    donationStatuses: number | null;
  };
}

// Initial state
const initialState: AppDataState = {
  employers: [],
  donationStatuses: [],
  loading: {
    employers: false,
    donationStatuses: false
  },
  errors: {
    employers: null,
    donationStatuses: null
  },
  lastFetched: {
    employers: null,
    donationStatuses: null
  }
};

// Create the main store
const appDataStore: Writable<AppDataState> = writable(initialState);

// Derived stores for easier access
const employers: Readable<Employer[]> = derived(
  appDataStore,
  $appData => $appData.employers
);

const donationStatuses: Readable<DonationStatus[]> = derived(
  appDataStore,
  $appData => $appData.donationStatuses
);

const isLoadingEmployers: Readable<boolean> = derived(
  appDataStore,
  $appData => $appData.loading.employers
);

const isLoadingDonationStatuses: Readable<boolean> = derived(
  appDataStore,
  $appData => $appData.loading.donationStatuses
);

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper function to check if data is still fresh
function isCacheValid(lastFetched: number | null): boolean {
  if (!lastFetched) return false;
  return Date.now() - lastFetched < CACHE_DURATION;
}

// App data store actions
const appData = {
  // Get employers for a specific user
  fetchEmployers: async (userUuid: string, forceRefresh = false) => {
    // Get current state first
    let currentState: AppDataState;
    const unsubscribe = appDataStore.subscribe(state => {
      currentState = state;
    });
    unsubscribe();

    // Check if we already have fresh data and don't need to force refresh
    if (!forceRefresh && currentState!.employers.length > 0 && isCacheValid(currentState!.lastFetched.employers)) {
      return { success: true };
    }

    // Set loading state
    appDataStore.update(state => ({
      ...state,
      loading: { ...state.loading, employers: true },
      errors: { ...state.errors, employers: null }
    }));

    try {
      // Fetch employers from API
      const result = await EmployerService.getUserEmployers(userUuid);
      
      if (result.success && result.employers) {
        appDataStore.update(state => ({
          ...state,
          employers: result.employers!,
          loading: { ...state.loading, employers: false },
          errors: { ...state.errors, employers: null },
          lastFetched: { ...state.lastFetched, employers: Date.now() }
        }));
        return { success: true };
      } else {
        const error = 'Failed to load employers';
        appDataStore.update(state => ({
          ...state,
          loading: { ...state.loading, employers: false },
          errors: { ...state.errors, employers: error }
        }));
        return { success: false, error };
      }
    } catch (error) {
      appDataStore.update(state => ({
        ...state,
        loading: { ...state.loading, employers: false },
        errors: { ...state.errors, employers: 'Network error while fetching employers' }
      }));
      return { success: false, error };
    }
  },

  // Get donation statuses
  fetchDonationStatuses: async (forceRefresh = false) => {
    // Get current state first
    let currentState: AppDataState;
    const unsubscribe = appDataStore.subscribe(state => {
      currentState = state;
    });
    unsubscribe();

    // Check if we already have fresh data and don't need to force refresh
    if (!forceRefresh && currentState!.donationStatuses.length > 0 && isCacheValid(currentState!.lastFetched.donationStatuses)) {
      return { success: true };
    }

    // Set loading state
    appDataStore.update(state => ({
      ...state,
      loading: { ...state.loading, donationStatuses: true },
      errors: { ...state.errors, donationStatuses: null }
    }));

    try {
      // Fetch donation statuses from API
      const response = await fetch('/api/v1/donation-request-status');
      const result = await response.json();
      
      if (result.success && result.data) {
        appDataStore.update(state => ({
          ...state,
          donationStatuses: result.data,
          loading: { ...state.loading, donationStatuses: false },
          errors: { ...state.errors, donationStatuses: null },
          lastFetched: { ...state.lastFetched, donationStatuses: Date.now() }
        }));
        return { success: true };
      } else {
        const error = 'Failed to load donation statuses';
        appDataStore.update(state => ({
          ...state,
          loading: { ...state.loading, donationStatuses: false },
          errors: { ...state.errors, donationStatuses: error }
        }));
        return { success: false, error };
      }
    } catch (error) {
      appDataStore.update(state => ({
        ...state,
        loading: { ...state.loading, donationStatuses: false },
        errors: { ...state.errors, donationStatuses: 'Network error while fetching donation statuses' }
      }));
      return { success: false, error };
    }
  },

  // Clear all data (useful for logout)
  clearAll: () => {
    appDataStore.set(initialState);
  },

  // Clear specific data
  clearEmployers: () => {
    appDataStore.update(state => ({
      ...state,
      employers: [],
      lastFetched: { ...state.lastFetched, employers: null },
      errors: { ...state.errors, employers: null }
    }));
  },

  clearDonationStatuses: () => {
    appDataStore.update(state => ({
      ...state,
      donationStatuses: [],
      lastFetched: { ...state.lastFetched, donationStatuses: null },
      errors: { ...state.errors, donationStatuses: null }
    }));
  },

  // Get current state (useful for checking cache status)
  getState: () => {
    let currentState: AppDataState = initialState;
    appDataStore.subscribe(state => {
      currentState = state;
    })();
    return currentState;
  }
};

export {
  appDataStore,
  employers,
  donationStatuses,
  isLoadingEmployers,
  isLoadingDonationStatuses,
  appData
}; 